interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

export const translations: Translations = {
  en: {
    // Hero Section
    'hero.title': 'Shape the',
    'hero.titleHighlight': 'Sustainable Future',
    'hero.description': 'Achieve your sustainability goals with our AI-powered ESG reporting and carbon analysis platform',
    'hero.demoButton': 'Free Demo',
    'hero.learnMore': 'Learn More',
    'hero.stats.users': 'Active Users',
    'hero.stats.data': 'Data Analyzed',
    'hero.stats.countries': 'Countries',
    'hero.imageAlt': 'Dashboard Preview',

    // Navigation
    'nav.features': 'Features',
    'nav.solutions': 'Solutions',
    'nav.pricing': 'Pricing',
    'nav.about': 'About',
    'nav.product': 'Product',
    'nav.demo': 'Demo',
    'nav.contact': 'Contact',
    'nav.signin': 'Sign In',
    'nav.signout': 'Sign Out',
    'nav.dashboard': 'Dashboard',
    'nav.getStarted': 'Get Started',

    // Features
    'features.title': 'Features',
    'features.subtitle': 'Powerful tools to help you on your sustainability journey',
    'features.esg': 'ESG Reporting',
    'features.carbon': 'Carbon Management',
    'features.learnMore': 'Learn More',

    // ESG Features
    'features.esg.reporting.title': 'Automated Reporting',
    'features.esg.reporting.description': 'Generate ESG reports automatically with AI',
    'features.esg.reporting.detail1': 'SASB and GRI standards compliance',
    'features.esg.reporting.detail2': 'Customizable report templates',
    'features.esg.reporting.detail3': 'Automatic data validation',

    'features.esg.performance.title': 'Performance Analysis',
    'features.esg.performance.description': 'Analyze your ESG performance in detail',
    'features.esg.performance.detail1': 'Industry benchmarking',
    'features.esg.performance.detail2': 'Trend analysis',
    'features.esg.performance.detail3': 'Risk assessment',

    'features.esg.tracking.title': 'Goal Tracking',
    'features.esg.tracking.description': 'Monitor your sustainability goals in real-time',
    'features.esg.tracking.detail1': 'Customizable KPIs',
    'features.esg.tracking.detail2': 'Progress reports',
    'features.esg.tracking.detail3': 'Alert system',

    // Carbon Features
    'features.carbon.analysis.title': 'Emissions Analysis',
    'features.carbon.analysis.description': 'Measure and analyze your carbon emissions',
    'features.carbon.analysis.detail1': 'Scope 1, 2, and 3 emissions',
    'features.carbon.analysis.detail2': 'Automatic data collection',
    'features.carbon.analysis.detail3': 'Emission forecasting',

    'features.carbon.risk.title': 'Risk Management',
    'features.carbon.risk.description': 'Predict and manage climate risks',
    'features.carbon.risk.detail1': 'Scenario analysis',
    'features.carbon.risk.detail2': 'Risk assessment',
    'features.carbon.risk.detail3': 'Compliance strategies',

    'features.carbon.reduction.title': 'Reduction Strategies',
    'features.carbon.reduction.description': 'Reduce emissions with smart recommendations',
    'features.carbon.reduction.detail1': 'AI-powered suggestions',
    'features.carbon.reduction.detail2': 'Cost analysis',
    'features.carbon.reduction.detail3': 'Project tracking',

    // Solutions
    'solutions.title': 'Comprehensive Solutions',
    'solutions.subtitle': 'All the tools you need to reach your sustainability goals',
    'solutions.esgManagement': 'ESG Data Management',
    'solutions.reporting': 'Sustainability Reporting',
    'solutions.analysis': 'Corporate Analysis',
    'solutions.netZero': 'Net Zero Strategy',
    'solutions.learnMore': 'Learn More',

    // Pricing
    'pricing.title': 'Transparent Pricing',
    'pricing.subtitle': 'Scalable plans that fit your needs',
    'pricing.starter': 'Starter',
    'pricing.professional': 'Professional',
    'pricing.enterprise': 'Enterprise',
    'pricing.popular': 'Most Popular',
    'pricing.getStarted': 'Get Started',
    'pricing.contactUs': 'Contact Us',

    // Auth
    'auth.signin': 'Sign in to your account',
    'auth.email': 'Email address',
    'auth.password': 'Password',
    'auth.submit': 'Sign in',
    'auth.error': 'Invalid credentials',

    // Footer
    'footer.about': 'About Sustainalyze',
    'footer.aboutText': 'AI-powered ESG and carbon management platform shaping the sustainable future',
    'footer.solutions': 'Solutions',
    'footer.company': 'Company',
    'footer.contact': 'Contact Us',
    'footer.rights': '© 2024 Sustainalyze. All rights reserved.',

    // Landing: Hero
    'landing.hero.eyebrow': 'TSRS phase-in + CBAM',
    'landing.hero.title': 'Audit-ready TSRS & CBAM reporting for exporters',
    'landing.hero.subtitle': 'Bring carbon, water and ESG data into one platform and produce standards-compliant reports — without consultancy costs.',
    'landing.hero.ctaPrimary': 'Start free',
    'landing.hero.ctaSecondary': 'See it in action',
    'landing.hero.ctaContact': 'Talk to us',

    // Landing: Features
    'landing.features.title': 'Your plan for TSRS & CBAM compliance',
    'landing.features.subtitle': 'From messy spreadsheets to audit-ready reports — one platform, one workflow.',
    'landing.features.import.title': 'Bring your data in',
    'landing.features.import.description': 'CSV/Excel import with automatic emission-factor matching.',
    'landing.features.reporting.title': 'Standards-compliant reports',
    'landing.features.reporting.description': 'GHG Protocol / GRI / ESRS-aligned carbon, water and ESG reports.',
    'landing.features.readiness.title': 'Ready for TSRS & CBAM',
    'landing.features.readiness.description': 'Outputs positioned for Turkish and EU exporter obligations.',
    'landing.features.evaluation.title': 'Compliance evaluation',
    'landing.features.evaluation.description': 'Score a report against TSRS/GRI/ESRS (Professional plan).',

    // Landing: Solutions
    'landing.solutions.eyebrow': 'Complete Solution Suite',
    'landing.solutions.title': 'Everything an exporter needs to report',
    'landing.solutions.subtitle': 'Purpose-built modules for TSRS reporting, CBAM-exporter reporting, carbon/water/ESG tracking and compliance evaluation.',
    'landing.solutions.tsrs.title': 'TSRS Reporting',
    'landing.solutions.tsrs.description': 'Prepare disclosures aligned with Türkiye Sürdürülebilirlik Raporlama Standartları as the transition period unfolds.',
    'landing.solutions.tsrs.feature1': 'Standard-mapped data fields',
    'landing.solutions.tsrs.feature2': 'Automated data validation',
    'landing.solutions.tsrs.feature3': 'Audit-ready export',
    'landing.solutions.cbam.title': 'CBAM Exporter Reporting',
    'landing.solutions.cbam.description': 'Produce the embedded-emissions data your EU buyers need for Carbon Border Adjustment Mechanism declarations.',
    'landing.solutions.cbam.feature1': 'Product-level emissions data',
    'landing.solutions.cbam.feature2': 'EU declaration-ready output',
    'landing.solutions.cbam.feature3': 'Supplier data collection',
    'landing.solutions.modules.title': 'Carbon, Water & ESG Modules',
    'landing.solutions.modules.description': 'Track Scope 1/2/3 emissions, ISO 14046 water footprint and ESG metrics in one platform.',
    'landing.solutions.modules.feature1': 'Multi-scope carbon accounting',
    'landing.solutions.modules.feature2': 'Water footprint tracking',
    'landing.solutions.modules.feature3': 'ESG intensity metrics',
    'landing.solutions.evaluation.title': 'Compliance Evaluation',
    'landing.solutions.evaluation.description': 'Score an existing sustainability report against TSRS, GRI or ESRS and surface the gaps before an auditor does.',
    'landing.solutions.evaluation.feature1': 'Standard-by-standard scoring',
    'landing.solutions.evaluation.feature2': 'Gap identification',
    'landing.solutions.evaluation.feature3': 'Auditable methodology',

    // Landing: Validation band
    'landing.validation.title': 'Validated against real reports',
    'landing.validation.body': 'Our carbon, water and intensity calculations are validated against the published sustainability reports of leading industrial firms.',
    'landing.validation.disclaimer': 'These firms are benchmark/validation sources; this does not imply they are customers or partners.',

    // Landing: Product tour
    'landing.tour.title': 'See it in action',
    'landing.tour.subtitle': 'A quick walkthrough of the dashboard, compliance evaluation and carbon calculator.',
    'landing.tour.step1.caption': 'Dashboard overview — all your carbon, water and ESG metrics in one view.',
    'landing.tour.step2.caption': 'Compliance evaluation — score an existing report against TSRS, GRI or ESRS.',
    'landing.tour.step3.caption': 'Carbon calculator — automatic emission-factor matching as you enter data.',

    // Contact form
    'contact.heading': 'Talk to us',
    'contact.subheading': "Tell us about your reporting needs and we'll get back to you shortly.",
    'contact.nameLabel': 'Name',
    'contact.companyLabel': 'Company',
    'contact.emailLabel': 'Email',
    'contact.messageLabel': 'Message',
    'contact.honeypotLabel': 'Website',
    'contact.submit': 'Send message',
    'contact.success': "Thanks — we've received your message and will be in touch soon.",
    'contact.error': 'Something went wrong sending your message. Please try again or email hello@sustainalyze.app.',
    'contact.errorRequired': 'This field is required',
    'contact.errorEmail': 'Enter a valid email address',

    // Footer: legal
    'footer.privacy': 'Privacy Policy',
    'footer.kvkk': 'KVKK Disclosure',
  },
  tr: {
    // Hero Section
    'hero.title': 'Sürdürülebilir',
    'hero.titleHighlight': 'Geleceği Şekillendir',
    'hero.description': 'Yapay zeka destekli ESG raporlama ve karbon analizi platformu ile sürdürülebilirlik hedeflerinize ulaşın',
    'hero.demoButton': 'Ücretsiz Demo',
    'hero.learnMore': 'Daha Fazla Bilgi',
    'hero.stats.users': 'Aktif Kullanıcı',
    'hero.stats.data': 'Analiz Edilen Veri',
    'hero.stats.countries': 'Ülke',
    'hero.imageAlt': 'Panel Önizleme',

    // Navigation
    'nav.features': 'Özellikler',
    'nav.solutions': 'Çözümler',
    'nav.pricing': 'Fiyatlandırma',
    'nav.about': 'Hakkımızda',
    'nav.product': 'Ürün',
    'nav.demo': 'Demo',
    'nav.contact': 'İletişim',
    'nav.signin': 'Giriş Yap',
    'nav.signout': 'Çıkış Yap',
    'nav.dashboard': 'Kontrol Paneli',
    'nav.getStarted': 'Hemen Başla',

    // Features
    'features.title': 'Özellikler',
    'features.subtitle': 'Sürdürülebilirlik yolculuğunuzda size yardımcı olacak güçlü araçlar',
    'features.esg': 'ESG Raporlama',
    'features.carbon': 'Karbon Yönetimi',
    'features.learnMore': 'Daha Fazla Bilgi',

    // ESG Features
    'features.esg.reporting.title': 'Otomatik Raporlama',
    'features.esg.reporting.description': 'ESG raporlarınızı yapay zeka ile otomatik oluşturun',
    'features.esg.reporting.detail1': 'SASB ve GRI standartlarına uyum',
    'features.esg.reporting.detail2': 'Özelleştirilebilir rapor şablonları',
    'features.esg.reporting.detail3': 'Otomatik veri doğrulama',

    'features.esg.performance.title': 'Performans Analizi',
    'features.esg.performance.description': 'ESG performansınızı detaylı olarak analiz edin',
    'features.esg.performance.detail1': 'Sektör karşılaştırmaları',
    'features.esg.performance.detail2': 'Trend analizi',
    'features.esg.performance.detail3': 'Risk değerlendirmesi',

    'features.esg.tracking.title': 'Hedef Takibi',
    'features.esg.tracking.description': 'Sürdürülebilirlik hedeflerinizi gerçek zamanlı izleyin',
    'features.esg.tracking.detail1': 'Özelleştirilebilir KPIlar',
    'features.esg.tracking.detail2': 'İlerleme raporları',
    'features.esg.tracking.detail3': 'Uyarı sistemi',

    // Carbon Features
    'features.carbon.analysis.title': 'Emisyon Analizi',
    'features.carbon.analysis.description': 'Karbon emisyonlarınızı ölçün ve analiz edin',
    'features.carbon.analysis.detail1': 'Scope 1, 2 ve 3 emisyonları',
    'features.carbon.analysis.detail2': 'Otomatik veri toplama',
    'features.carbon.analysis.detail3': 'Emisyon tahminleri',

    'features.carbon.risk.title': 'Risk Yönetimi',
    'features.carbon.risk.description': 'İklim risklerini öngörün ve yönetin',
    'features.carbon.risk.detail1': 'Senaryo analizi',
    'features.carbon.risk.detail2': 'Risk değerlendirmesi',
    'features.carbon.risk.detail3': 'Uyum stratejileri',

    'features.carbon.reduction.title': 'Azaltım Stratejileri',
    'features.carbon.reduction.description': 'Akıllı önerilerle emisyonlarınızı azaltın',
    'features.carbon.reduction.detail1': 'Yapay zeka önerileri',
    'features.carbon.reduction.detail2': 'Maliyet analizi',
    'features.carbon.reduction.detail3': 'Proje takibi',

    // Solutions
    'solutions.title': 'Kapsamlı Çözümler',
    'solutions.subtitle': 'Sürdürülebilirlik hedeflerinize ulaşmanız için ihtiyacınız olan tüm araçlar',
    'solutions.esgManagement': 'ESG Veri Yönetimi',
    'solutions.reporting': 'Sürdürülebilirlik Raporlaması',
    'solutions.analysis': 'Kurumsal Analiz',
    'solutions.netZero': 'Net Sıfır Stratejisi',
    'solutions.learnMore': 'Daha Fazla Bilgi',

    // Pricing
    'pricing.title': 'Şeffaf Fiyatlandırma',
    'pricing.subtitle': 'İhtiyaçlarınıza uygun, ölçeklenebilir planlar',
    'pricing.starter': 'Başlangıç',
    'pricing.professional': 'Profesyonel',
    'pricing.enterprise': 'Kurumsal',
    'pricing.popular': 'En Popüler',
    'pricing.getStarted': 'Hemen Başla',
    'pricing.contactUs': 'İletişime Geç',

    // Auth
    'auth.signin': 'Hesabınıza giriş yapın',
    'auth.email': 'E-posta adresi',
    'auth.password': 'Şifre',
    'auth.submit': 'Giriş yap',
    'auth.error': 'Geçersiz kimlik bilgileri',

    // Footer
    'footer.about': 'Sustainalyze Hakkında',
    'footer.aboutText': 'Sürdürülebilir geleceği şekillendiren yapay zeka destekli ESG ve karbon yönetimi platformu',
    'footer.solutions': 'Çözümler',
    'footer.company': 'Şirket',
    'footer.contact': 'İletişim',
    'footer.rights': '© 2024 Sustainalyze. Tüm hakları saklıdır.',

    // Landing: Hero
    'landing.hero.eyebrow': 'TSRS geçiş dönemi + CBAM',
    'landing.hero.title': 'İhracatçılar için denetime hazır TSRS ve CBAM raporlaması',
    'landing.hero.subtitle': 'Karbon, su ve ESG verinizi tek platformda toplayın; danışmanlık masrafı olmadan standartlara uygun raporlar üretin.',
    'landing.hero.ctaPrimary': 'Ücretsiz başla',
    'landing.hero.ctaSecondary': 'Nasıl çalıştığını gör',
    'landing.hero.ctaContact': 'Bize ulaşın',

    // Landing: Features
    'landing.features.title': 'TSRS ve CBAM uyumu için planınız',
    'landing.features.subtitle': 'Dağınık tablolardan denetime hazır raporlara — tek platform, tek iş akışı.',
    'landing.features.import.title': 'Verinizi içeri aktarın',
    'landing.features.import.description': 'Otomatik emisyon faktörü eşleştirmeli CSV/Excel içe aktarma.',
    'landing.features.reporting.title': 'Standartlara uygun raporlar',
    'landing.features.reporting.description': 'GHG Protocol / GRI / ESRS uyumlu karbon, su ve ESG raporları.',
    'landing.features.readiness.title': "TSRS ve CBAM'a hazır",
    'landing.features.readiness.description': 'Türk ve AB ihracatçı yükümlülükleri için konumlandırılmış çıktılar.',
    'landing.features.evaluation.title': 'Uyum değerlendirmesi',
    'landing.features.evaluation.description': "Bir raporu TSRS/GRI/ESRS'e karşı puanlayın (Profesyonel plan).",

    // Landing: Solutions
    'landing.solutions.eyebrow': 'Kapsamlı Çözüm Paketi',
    'landing.solutions.title': 'Bir ihracatçının rapor için ihtiyaç duyduğu her şey',
    'landing.solutions.subtitle': 'TSRS raporlaması, CBAM ihracatçı raporlaması, karbon/su/ESG takibi ve uyum değerlendirmesi için özel olarak tasarlanmış modüller.',
    'landing.solutions.tsrs.title': 'TSRS Raporlama',
    'landing.solutions.tsrs.description': 'Geçiş dönemi ilerlerken Türkiye Sürdürülebilirlik Raporlama Standartları ile uyumlu açıklamalar hazırlayın.',
    'landing.solutions.tsrs.feature1': 'Standarda eşlenmiş veri alanları',
    'landing.solutions.tsrs.feature2': 'Otomatik veri doğrulama',
    'landing.solutions.tsrs.feature3': 'Denetime hazır dışa aktarma',
    'landing.solutions.cbam.title': 'CBAM İhracatçı Raporlaması',
    'landing.solutions.cbam.description': 'AB alıcılarınızın Sınırda Karbon Düzenleme Mekanizması beyanları için ihtiyaç duyduğu gömülü emisyon verisini üretin.',
    'landing.solutions.cbam.feature1': 'Ürün bazlı emisyon verisi',
    'landing.solutions.cbam.feature2': 'AB beyanına hazır çıktı',
    'landing.solutions.cbam.feature3': 'Tedarikçi veri toplama',
    'landing.solutions.modules.title': 'Karbon, Su ve ESG Modülleri',
    'landing.solutions.modules.description': 'Kapsam 1/2/3 emisyonlarını, ISO 14046 su ayak izini ve ESG metriklerini tek platformda izleyin.',
    'landing.solutions.modules.feature1': 'Çok kapsamlı karbon muhasebesi',
    'landing.solutions.modules.feature2': 'Su ayak izi takibi',
    'landing.solutions.modules.feature3': 'ESG yoğunluk metrikleri',
    'landing.solutions.evaluation.title': 'Uyum Değerlendirmesi',
    'landing.solutions.evaluation.description': 'Mevcut bir sürdürülebilirlik raporunu TSRS, GRI veya ESRS karşısında puanlayın ve boşlukları bir denetçiden önce ortaya çıkarın.',
    'landing.solutions.evaluation.feature1': 'Standart bazında puanlama',
    'landing.solutions.evaluation.feature2': 'Boşluk tespiti',
    'landing.solutions.evaluation.feature3': 'Denetlenebilir metodoloji',

    // Landing: Validation band
    'landing.validation.title': 'Gerçek raporlarla doğrulandı',
    'landing.validation.body': 'Karbon, su ve yoğunluk hesaplarımız önde gelen sanayi şirketlerinin yayımlanmış sürdürülebilirlik raporlarıyla doğrulanır.',
    'landing.validation.disclaimer': 'Bu şirketler referans/doğrulama kaynağıdır; müşteri veya iş ortağı olduklarını ima etmez.',

    // Landing: Product tour
    'landing.tour.title': 'Nasıl çalıştığını görün',
    'landing.tour.subtitle': 'Kontrol paneli, uyum değerlendirmesi ve karbon hesaplayıcısına hızlı bir bakış.',
    'landing.tour.step1.caption': 'Kontrol paneli özeti — tüm karbon, su ve ESG metrikleriniz tek ekranda.',
    'landing.tour.step2.caption': 'Uyum değerlendirmesi — mevcut bir raporu TSRS, GRI veya ESRS karşısında puanlayın.',
    'landing.tour.step3.caption': 'Karbon hesaplayıcı — veri girerken otomatik emisyon faktörü eşleştirme.',

    // Contact form
    'contact.heading': 'Bize ulaşın',
    'contact.subheading': 'Raporlama ihtiyaçlarınızı anlatın, en kısa sürede size dönelim.',
    'contact.nameLabel': 'Ad Soyad',
    'contact.companyLabel': 'Şirket',
    'contact.emailLabel': 'E-posta',
    'contact.messageLabel': 'Mesaj',
    'contact.honeypotLabel': 'Web sitesi',
    'contact.submit': 'Mesaj gönder',
    'contact.success': 'Teşekkürler — mesajınızı aldık, en kısa sürede size dönüş yapacağız.',
    'contact.error': 'Mesajınız gönderilirken bir sorun oluştu. Lütfen tekrar deneyin veya hello@sustainalyze.app adresine e-posta gönderin.',
    'contact.errorRequired': 'Bu alan zorunludur',
    'contact.errorEmail': 'Geçerli bir e-posta adresi girin',

    // Footer: legal
    'footer.privacy': 'Gizlilik Politikası',
    'footer.kvkk': 'KVKK Aydınlatma Metni',
  },
};